"use client";

import { FormEvent, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { toast } from "@/components/AppToaster";

const gotraList = [
  "Airan (ऐरण)",
  "Bansal (बंसल)",
  "Bhandal (भंदल)",
  "Bindal (बिंदल)",
  "Dharan (धारण)",
  "Garg (गर्ग)",
  "Goyal (गोयल)",
  "Goyan (गोयन)",
  "Jindal (जिंदल)",
  "Kansal (कंसल)",
  "Kuchhal (कुच्छल)",
  "Madhukul (मधुकुल)",
  "Mangal (मंगल)",
  "Mittal (मित्तल)",
  "Nangal (नांगल)",
  "Singhal (सिंघल)",
  "Tayal (तायल)",
  "Tingal (टिंगल)",
];

function PasswordEyeIcon({ off }: { off?: boolean }) {
  if (off) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M2 4.3 4.3 2l17.7 17.7-2.3 2.3-3.1-3.1A11.5 11.5 0 0 1 12 20C5 20 1.3 12 1.3 12a19.6 19.6 0 0 1 5.4-5.9L2 4.3Zm8.1 4.1 1.5 1.5a2.5 2.5 0 0 1 3.1 3.1l1.5 1.5a4.5 4.5 0 0 0-6.1-6.1ZM12 7a11.7 11.7 0 0 1 9.7 5s-1.6 3.4-4.6 5.4l-1.6-1.6A4.5 4.5 0 0 0 12 7.4V7Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 5c7 0 10.7 7 10.7 7s-3.7 7-10.7 7S1.3 12 1.3 12 5 5 12 5Zm0 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z"
        fill="currentColor"
      />
    </svg>
  );
}
export default function RegisterPage() {
  const router = useRouter();
  const [memberName, setMemberName] = useState("User");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  async function usernameTakenMessage(username: string) {
    const value = username.trim();
    if (!value) {
      setUsernameError("");
      return "";
    }
    if (!/^[A-Za-z0-9_]{3,30}$/.test(value)) {
      const message = "Username can use only letters, numbers, and underscore.";
      setUsernameError(message);
      return message;
    }
    const response = await fetch("http://localhost:5002/api/auth/username-available", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: value }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.available) {
      const message = data?.message || "This username is already taken. Please choose a different username.";
      setUsernameError(message);
      return message;
    }
    setUsernameError("");
    return "";
  }

  async function handleRegister(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const fullName = String(formData.get("full_name") || "").trim();
    const username = String(formData.get("username") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const usernameField = document.getElementById("usernameField");
    const emailField = document.getElementById("emailField");
    const taken = await usernameTakenMessage(username);
    if (taken) {
      usernameField?.classList.add("input-error-highlight");
      usernameField?.focus();
      toast.error(taken);
      return;
    }

    setShowSuccess(false);
    usernameField?.classList.remove("input-error-highlight");
    emailField?.classList.remove("input-error-highlight");
    setSubmitting(true);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 35_000);

    try {
      const response = await fetch("http://localhost:5002/api/auth/register", {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: fullName,
          gotra: String(formData.get("gotra") || "").trim(),
          mobile_no: String(formData.get("mobile_no") || "").trim(),
          native_place: String(formData.get("native_place") || "").trim(),
          username,
          password: String(formData.get("password") || ""),
          email,
        }),
      });
      const data = await response.json().catch(() => null);

      if (data?.success && data.otpRequired) {
        setMemberName(fullName || "User");
        setOtpEmail(data.email || email);
        setOtpValue("");
        setShowOtp(true);
        toast.success("OTP sent to your Gmail.");
        return;
      }

      if (data?.success) {
        setMemberName(fullName || "User");
        toast.success("Registration successful!");
        setShowSuccess(true);
        form.reset();
        window.setTimeout(() => {
          router.push("/login");
        }, 5000);
        return;
      }

      const message = data?.message || "Server execution pipeline failure. Please re-submit.";
      toast.error(message);
      if (message.toLowerCase().includes("username")) {
        setUsernameError(message);
        usernameField?.classList.add("input-error-highlight");
        usernameField?.focus();
      }
      if (message.toLowerCase().includes("gmail") || message.toLowerCase().includes("email")) {
        emailField?.classList.add("input-error-highlight");
        emailField?.focus();
      }
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === "AbortError";
      toast.error(
        aborted
          ? "OTP request timed out. Please check your connection and try again."
          : "Server execution pipeline failure. Please re-submit."
      );
    } finally {
      window.clearTimeout(timeoutId);
      setSubmitting(false);
    }
  }

  async function handleVerifyOtp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const otp = otpValue.trim();
    if (!/^\d{6}$/.test(otp)) {
      toast.error("Please enter the 6-digit OTP from your Gmail.");
      return;
    }
    setVerifying(true);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 35_000);
    try {
      const response = await fetch("http://localhost:5002/api/auth/register/verify-otp", {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: otpEmail, otp }),
      });
      const data = await response.json().catch(() => null);
      if (!data?.success) {
        toast.error(data?.message || "Invalid OTP. Please try again.");
        return;
      }
      setMemberName(data.full_name || memberName);
      setShowOtp(false);
      setShowSuccess(true);
      toast.success("Registration complete. Welcome email sent.");
      const form = document.getElementById("miniRegisterForm") as HTMLFormElement | null;
      form?.reset();
      window.setTimeout(() => {
        router.push("/login");
      }, 5000);
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === "AbortError";
      toast.error(aborted ? "OTP verification timed out. Please try again." : "Could not verify OTP. Please try again.");
    } finally {
      window.clearTimeout(timeoutId);
      setVerifying(false);
    }
  }

  function digitsOnly(event: FormEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    input.value = input.value.replace(/[^0-9]/g, "").slice(0, 10);
  }

  function unlockField(event: FormEvent<HTMLInputElement>) {
    event.currentTarget.removeAttribute("readOnly");
  }

  function capitalizeName(event: FormEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    if (!input.value) return;
    input.value = input.value
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  return (
    <>
      <style>{`
        .hint-text { font-size: 11px; color: #777; margin-top: 4px; }
        .capitalize-input { text-transform: capitalize; }
        .input-error-highlight {
          border-color: #E53E3E !important;
          box-shadow: 0 0 0 3px rgba(229, 62, 94, 0.15) !important;
          background-color: #FFF5F5 !important;
        }
        .field-error { margin: 6px 0 0; color: #C53030; font-size: 12.5px; font-weight: 600; }
        .success-modal, .otp-modal {
          display: none;
          position: fixed;
          z-index: 9999;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          background: rgba(11, 42, 82, 0.45);
          backdrop-filter: blur(4px);
          justify-content: center;
          align-items: center;
          padding: 16px;
          box-sizing: border-box;
        }
        .success-modal-content, .otp-modal-content {
          background: #FFFDF9;
          padding: 32px 28px;
          border-radius: 18px;
          width: 100%;
          max-width: 440px;
          text-align: center;
          box-shadow: 0 18px 40px rgba(11, 42, 82, 0.18);
          animation: modalFadeIn 0.35s ease-out;
          border-top: 5px solid #E65100;
          box-sizing: border-box;
        }
        .success-modal-content { border-top-color: #10B981; }
        .success-modal-content h3, .otp-modal-content h3 {
          color: #0B2A52;
          font-family: var(--font-head);
          font-size: 24px;
          margin: 12px 0 10px 0;
        }
        .success-modal-content h3 { color: #10B981; }
        .success-modal-content p, .otp-modal-content p {
          color: #5c4f45;
          font-size: 14px;
          line-height: 1.55;
          margin-bottom: 0;
        }
        .otp-modal-content label {
          display: block;
          text-align: left;
          font-size: 13px;
          font-weight: 700;
          color: #0B2A52;
          margin: 18px 0 8px;
        }
        .otp-modal-content input {
          width: 100%;
          box-sizing: border-box;
          border: 1px solid #F0DCC4;
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 22px;
          letter-spacing: 0.35em;
          text-align: center;
          color: #0B2A52;
          background: #fff;
          font-family: Consolas, "Courier New", monospace;
        }
        .otp-actions {
          display: grid;
          gap: 10px;
          margin-top: 18px;
        }
        .otp-actions button {
          min-height: 46px;
          border-radius: 12px;
          border: none;
          font-weight: 700;
          cursor: pointer;
          font: inherit;
        }
        .otp-verify {
          background: #E65100;
          color: #fff;
        }
        .otp-cancel {
          background: #fff;
          color: #5c1d2a;
          border: 1px solid #d8c7b6 !important;
        }
        .password-field {
          position: relative;
        }
        .password-field input {
          padding-right: 48px;
        }
        .password-toggle {
          position: absolute;
          top: 50%;
          right: 10px;
          transform: translateY(-50%);
          width: 36px;
          height: 36px;
          border: none;
          background: transparent;
          color: #8D7568;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          padding: 0;
        }
        .password-toggle:hover {
          color: #E65100;
          background: rgba(230, 81, 0, 0.08);
        }
        @keyframes modalFadeIn {
          from { opacity: 0; transform: translateY(-24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="register-container auth-card">
        <h2
          style={{
            fontFamily: "var(--font-head)",
            marginTop: "-15px",
            marginBottom: "5px",
            color: "var(--dark-text)",
          }}
        >
          Join Samaj Directory
        </h2>
        <p style={{ color: "var(--primary-saffron)", fontSize: "13px", marginBottom: "25px", fontWeight: 500 }}>
          Create your basic account. An OTP will be sent to your Gmail to complete registration.
        </p>

        <form id="miniRegisterForm" autoComplete="off" onSubmit={handleRegister}>
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              name="full_name"
              className="capitalize-input"
              placeholder="Shri Amit Bansal"
              onBlur={capitalizeName}
              required
            />
          </div>

          <div className="form-group">
            <label>Gotra</label>
            <select name="gotra" required defaultValue="">
              <option value="" disabled>
                -- Choose your Gotra --
              </option>
              {gotraList.map((gotra) => (
                <option key={gotra} value={gotra.split(" ")[0]}>
                  {gotra}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Mobile No. / WhatsApp</label>
            <input
              type="tel"
              name="mobile_no"
              placeholder="10-digit number"
              maxLength={10}
              pattern="[0-9]{10}"
              inputMode="numeric"
              onInput={digitsOnly}
              required
            />
          </div>

          <div className="form-group">
            <label>Native Place (Mool Gaon)</label>
            <input
              type="text"
              name="native_place"
              className="capitalize-input"
              placeholder="e.g. Agroha, Hisar"
              onBlur={capitalizeName}
              required
            />
          </div>

          <div className="form-group">
            <label>Gmail</label>
            <input
              type="email"
              id="emailField"
              name="email"
              placeholder="name@gmail.com"
              autoComplete="email"
              onInput={(event) => event.currentTarget.classList.remove("input-error-highlight")}
              required
            />
          </div>

          <div className="form-group">
            <label>Choose Username (Login ID)</label>
            <input
              type="text"
              id="usernameField"
              name="username"
              placeholder="e.g. amit8080"
              autoComplete="off"
              readOnly
              onFocus={unlockField}
              onInput={(event) => {
                event.currentTarget.classList.remove("input-error-highlight");
                setUsernameError("");
              }}
              onBlur={(event) => {
                const field = event.currentTarget;
                void usernameTakenMessage(field.value).then((message) => {
                  field.classList.toggle("input-error-highlight", Boolean(message));
                });
              }}
              required
            />
            {usernameError ? <p className="field-error">{usernameError}</p> : null}
          </div>

          <div className="form-group">
            <label htmlFor="register-password">Set Password</label>
            <div className="password-field">
              <input
                id="register-password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="••••••••"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <PasswordEyeIcon off={showPassword} />
              </button>
            </div>
          </div>

          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Sending OTP..." : "Submit Registration Request"}
          </button>
        </form>

        <p style={{ textAlign: "center", marginTop: "20px", fontSize: "13px", color: "var(--light-text)" }}>
          Already registered?{" "}
          <Link href="/login" style={{ color: "var(--primary-saffron)", fontWeight: 600 }}>
            Login here
          </Link>
        </p>
      </div>

      <div className="otp-modal" style={{ display: showOtp ? "flex" : "none" }}>
        <div className="otp-modal-content">
          <h3>Verify Gmail OTP</h3>
          <p>
            A 6-digit code was sent to{" "}
            <strong style={{ color: "#0B2A52" }}>{otpEmail}</strong>. Enter it below to complete registration.
          </p>
          <form onSubmit={handleVerifyOtp}>
            <label htmlFor="register-otp">One-time password</label>
            <input
              id="register-otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otpValue}
              onChange={(event) => setOtpValue(event.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              required
            />
            <div className="otp-actions">
              <button type="submit" className="otp-verify" disabled={verifying}>
                {verifying ? "Verifying..." : "Verify & Complete"}
              </button>
              <button
                type="button"
                className="otp-cancel"
                onClick={() => {
                  setShowOtp(false);
                  setVerifying(false);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <div id="registrationSuccessModal" className="success-modal" style={{ display: showSuccess ? "flex" : "none" }}>
        <div className="success-modal-content">
          <span style={{ fontSize: "50px" }}>🎉</span>
          <h3>Registration Successful!</h3>
          <p>
            Dear{" "}
            <strong id="modalMemberName" style={{ color: "#2c3e50" }}>
              {memberName}
            </strong>
            , your registration is complete.
            <br />
            A welcome email has been sent to your Gmail. Please wait for approval.
          </p>
        </div>
      </div>
    </>
  );
}
